import { LogicGroupView } from '@/components/logic/LogicGroupView'
import { useContractGate } from '../hooks'
import type {
  GateGroup,
  GateMissingDocument,
  GateMissingField,
  GateMissingFieldVerification,
  GateMissingFinancing,
  GateMissingStatus,
} from '../types'
import { FINANCING_METRIC_LABEL, FINANCING_METRIC_NUMERIC } from '../types'

type AtomDisplay =
  | { kind: 'STATUS'; trackLabel: string; required: string; current: string | null }
  | { kind: 'DOCUMENT'; documentLabel: string }
  | { kind: 'FIELD'; fieldLabel: string }
  | { kind: 'FIELD_VERIFIED'; fieldLabel: string }
  | { kind: 'FINANCING'; label: string; numeric: boolean; required: number | null; current: number | null }

function buildAtoms(group: GateGroup): AtomDisplay[] {
  const status: AtomDisplay[] = group.missingStatus.map((m: GateMissingStatus) => ({
    kind: 'STATUS' as const,
    trackLabel: m.trackLabelPl ?? '(usunięty tor)',
    required: m.requiredValues.map(v => v.labelPl).join(' lub '),
    current: m.currentValueLabelPl,
  }))
  const documents: AtomDisplay[] = group.missingDocuments.map((m: GateMissingDocument) => ({
    kind: 'DOCUMENT' as const,
    documentLabel: m.documentTypeLabelPl ?? m.documentTypeCode ?? '(usunięty typ)',
  }))
  const fields: AtomDisplay[] = (group.missingFields ?? []).map((m: GateMissingField) => ({
    kind: 'FIELD' as const,
    fieldLabel: m.fieldCode,
  }))
  const fieldVerifications: AtomDisplay[] = (group.missingFieldVerifications ?? []).map(
    (m: GateMissingFieldVerification) => ({
      kind: 'FIELD_VERIFIED' as const,
      fieldLabel: m.fieldLabelPl ?? m.fieldCode,
    }),
  )
  const financing: AtomDisplay[] = (group.missingFinancing ?? []).map((m: GateMissingFinancing) => ({
    kind: 'FINANCING' as const,
    label: FINANCING_METRIC_LABEL[m.metric],
    numeric: FINANCING_METRIC_NUMERIC[m.metric],
    required: m.requiredValue,
    current: m.currentValue,
  }))
  return [...status, ...documents, ...fields, ...fieldVerifications, ...financing]
}

export function GateRequirementsPanel({ contractId }: { contractId: number }) {
  const q = useContractGate(contractId)
  if (q.isLoading || !q.data) return null

  const gate = q.data
  if (gate.canComplete && gate.groups.length === 0) return null

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
        Wymagania przejścia
      </div>
      {gate.canComplete ? (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
          ✓ Wszystkie wymagania spełnione
        </div>
      ) : (
        <LogicGroupView<AtomDisplay>
          groups={gate.groups.map(g => ({
            id: g.groupNo,
            satisfied: g.satisfied,
            // Always build atoms: satisfied groups will naturally collapse to [] (LogicGroupView
            // pokaże emptyMessage), unsatisfied groups with [] = data inconsistency -> warning.
            atoms: buildAtoms(g),
          }))}
          renderAtom={a => {
            switch (a.kind) {
              case 'STATUS':
                return (
                  <div>
                    <div className="font-medium text-red-700">✗ {a.trackLabel}</div>
                    <div className="text-slate-600">Wymagane: {a.required}</div>
                    <div className="text-slate-500">
                      Aktualne: {a.current ?? <span className="italic">brak</span>}
                    </div>
                  </div>
                )
              case 'DOCUMENT':
                return (
                  <div>
                    <div className="font-medium text-red-700">✗ Brak załącznika</div>
                    <div className="text-slate-600">Typ dokumentu: {a.documentLabel}</div>
                  </div>
                )
              case 'FIELD':
                return (
                  <div>
                    <div className="font-medium text-red-700">✗ Pole nie wypełnione</div>
                    <div className="text-slate-600">{a.fieldLabel}</div>
                  </div>
                )
              case 'FIELD_VERIFIED':
                return (
                  <div>
                    <div className="font-medium text-red-700">✗ Brak weryfikacji pola</div>
                    <div className="text-slate-600">{a.fieldLabel}</div>
                  </div>
                )
              case 'FINANCING':
                return (
                  <div>
                    <div className="font-medium text-red-700">✗ {a.label}</div>
                    {a.numeric ? (
                      <>
                        <div className="text-slate-600">Wymagane: ≥ {a.required}%</div>
                        <div className="text-slate-500">
                          Aktualne: {a.current != null ? `${a.current}%` : <span className="italic">brak</span>}
                        </div>
                      </>
                    ) : (
                      <div className="text-slate-600">Warunek niespełniony</div>
                    )}
                  </div>
                )
            }
          }}
          colorBySatisfaction
          emptyMessage="Grupa spełniona"
        />
      )}
    </div>
  )
}
